"use client"

import { useEffect, useMemo, useState } from "react"
import { formatDateTimeShort } from "@/utils/format-date"
import { Button, Checkbox, ScrollArea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/shad"
import { SimpleTable, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ui/shad/table"
import { cn } from "@ui/utils"
import { format } from "date-fns"
import { ChevronLeft, ChevronRight, Download, Loader2, Mail, RefreshCw } from "lucide-react"
import { toast } from "sonner"

import { useCancelBulkEmailRecipient, useFetchBulkEmail } from "@/hooks/api/bulk_emails"
import { FullscreenDialog } from "@/components/dialogs"
import PeekView from "@/components/peek-view"

interface BulkEmailDetailDialogProps {
  bulkEmailId: string
  onClose: () => void
}

type FilterType = "delivered" | "scheduled" | "opened" | "cancelled"

export function BulkEmailDetailDialog({ bulkEmailId, onClose }: BulkEmailDetailDialogProps) {
  const cancelRecipient = useCancelBulkEmailRecipient(bulkEmailId)

  const [activeFilter, setActiveFilter] = useState<FilterType>("delivered")
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isCancellingSelected, setIsCancellingSelected] = useState(false)
  const [previewRecipientId, setPreviewRecipientId] = useState<string | null>(null)
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(0)

  const {
    data: bulkEmail,
    isLoading,
    refetch,
    isRefetching,
  } = useFetchBulkEmail(bulkEmailId, {
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === "sending" || status === "scheduled" ? 2000 : false
    },
  })

  const recipients = useMemo(() => bulkEmail?.recipients ?? [], [bulkEmail?.recipients])

  // Stats for filter buttons
  const stats = useMemo(
    () => ({
      delivered: recipients.filter((r) => r.status === "sent").length,
      scheduled: recipients.filter((r) => r.status === "pending").length,
      opened: recipients.filter((r) => r.open_count > 0).length,
      cancelled: recipients.filter((r) => r.status === "skipped" || r.status === "failed").length,
    }),
    [recipients]
  )

  // Filtered recipients based on active filter
  const filteredRecipients = useMemo(() => {
    switch (activeFilter) {
      case "delivered":
        return recipients.filter((r) => r.status === "sent")
      case "scheduled":
        return recipients.filter((r) => r.status === "pending")
      case "opened":
        return recipients.filter((r) => r.open_count > 0)
      case "cancelled":
        return recipients.filter((r) => r.status === "skipped" || r.status === "failed")
    }
  }, [recipients, activeFilter])

  // Pagination
  const totalPages = Math.ceil(filteredRecipients.length / pageSize)
  const paginatedRecipients = useMemo(() => {
    const start = currentPage * pageSize
    return filteredRecipients.slice(start, start + pageSize)
  }, [filteredRecipients, currentPage, pageSize])

  // Get the recipient being previewed
  const previewRecipient = useMemo(
    () => (previewRecipientId ? recipients.find((r) => r.id === previewRecipientId) : null),
    [previewRecipientId, recipients]
  )

  // Render body with placeholders replaced
  const renderedBody = useMemo(() => {
    if (!bulkEmail?.body || !previewRecipient) return bulkEmail?.body || ""

    const nameParts = previewRecipient.name.split(" ")
    const firstName = nameParts[0] || ""
    const fullName = previewRecipient.name

    return bulkEmail.body
      .replace(/\{\{first_name\}\}/gi, firstName)
      .replace(/\{\{full_name\}\}/gi, fullName)
      .replace(/\{\{email\}\}/gi, previewRecipient.email || "")
  }, [bulkEmail?.body, previewRecipient])

  // Preselect first row when filter/page changes or data loads
  useEffect(() => {
    if (paginatedRecipients.length > 0) {
      setPreviewRecipientId(paginatedRecipients[0].id)
    } else {
      setPreviewRecipientId(null)
    }
  }, [paginatedRecipients])

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(0)
  }, [activeFilter])

  // Show checkboxes only on scheduled tab
  const showCheckboxes = activeFilter === "scheduled"

  // Selection helpers (for current page)
  const allSelectedOnPage =
    showCheckboxes && paginatedRecipients.length > 0 && paginatedRecipients.every((r) => selectedIds.has(r.id))
  const someSelectedOnPage = showCheckboxes && paginatedRecipients.some((r) => selectedIds.has(r.id))

  const handleSelectAllOnPage = (checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => new Set([...Array.from(prev), ...paginatedRecipients.map((r) => r.id)]))
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        paginatedRecipients.forEach((r) => next.delete(r.id))
        return next
      })
    }
  }

  const handleSelectRow = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }

  const handleCancelSelected = async () => {
    if (selectedIds.size === 0) return

    setIsCancellingSelected(true)
    try {
      await Promise.all(Array.from(selectedIds).map((id) => cancelRecipient.mutateAsync(id)))
      toast.success(`Cancelled ${selectedIds.size} scheduled email${selectedIds.size > 1 ? "s" : ""}`)
      setSelectedIds(new Set())
    } catch {
      // Error handled by global mutation error handler
    } finally {
      setIsCancellingSelected(false)
    }
  }

  // Clear selection when switching filters
  const handleFilterChange = (filter: FilterType) => {
    setActiveFilter(filter)
    setSelectedIds(new Set())
  }

  const handleRowClick = (recipientId: string) => {
    setPreviewRecipientId(recipientId)
  }

  const handleDownloadCsv = () => {
    if (!bulkEmail) return

    const headers = ["Email", "Full Name", "Status", "Opens", "Delivered At", "Scheduled For"]
    const rows = recipients.map((r) => [
      r.email || "",
      r.name,
      r.message_unavailable ? "Message unavailable" : r.status,
      r.open_count.toString(),
      r.syncbox_draft?.sent_at ? format(new Date(r.syncbox_draft.sent_at), "yyyy-MM-dd HH:mm:ss") : "",
      r.syncbox_draft?.scheduled_for ? format(new Date(r.syncbox_draft.scheduled_for), "yyyy-MM-dd HH:mm:ss") : "",
    ])

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
      .join("\n")

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `bulk-email-${bulkEmail.subject || "export"}-${format(new Date(), "yyyy-MM-dd")}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (isLoading || !bulkEmail) {
    return (
      <FullscreenDialog open onOpenChange={(open) => !open && onClose()} title="Bulk email details">
        <div className="flex h-96 items-center justify-center">
          <Loader2 className="text-muted-foreground size-6 animate-spin" />
        </div>
      </FullscreenDialog>
    )
  }

  return (
    <FullscreenDialog open onOpenChange={(open) => !open && onClose()} title="Bulk email details">
      <div className="flex h-full min-h-0 flex-1">
        {/* Left Panel - Email Preview */}
        <div className="flex w-[480px] shrink-0 flex-col border-r">
          <div className="flex h-[60px] items-center border-b px-6">
            <h2 className="truncate text-lg font-semibold">
              {previewRecipient ? `Preview for ${previewRecipient.name}` : "Email preview"}
            </h2>
          </div>
          {previewRecipient ? (
            <>
              <div className="border-b px-6 py-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-muted-foreground shrink-0 text-sm">Subject</span>
                  <span className="truncate text-sm font-medium">{bulkEmail.subject || "(No subject)"}</span>
                </div>
              </div>
              <ScrollArea className="min-h-0 flex-1">
                <div
                  className="prose prose-sm dark:prose-invert max-w-none p-6"
                  dangerouslySetInnerHTML={{ __html: renderedBody || "<p>No content</p>" }}
                />
              </ScrollArea>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6">
              <Mail className="text-muted-foreground size-12" />
              <p className="text-muted-foreground text-sm">Select a recipient to preview</p>
            </div>
          )}
        </div>

        {/* Right Panel - Stats & Table */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Header */}
          <div className="flex h-[60px] items-center justify-between border-b px-6">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Stats overview</h2>
              <Button variant="ghost" size="iconSm" onClick={() => refetch()} disabled={isRefetching}>
                <RefreshCw className={cn(isRefetching && "animate-spin")} />
                <span className="sr-only">Refresh</span>
              </Button>
              <Button variant="ghost" size="iconSm" onClick={handleDownloadCsv}>
                <Download />
                <span className="sr-only">Download CSV</span>
              </Button>
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="border-b px-6 py-4">
            <div className="grid grid-cols-4 overflow-hidden rounded-lg border">
              <FilterCard
                label="Delivered"
                value={stats.delivered}
                active={activeFilter === "delivered"}
                onClick={() => handleFilterChange("delivered")}
                position="left"
              />
              <FilterCard
                label="Scheduled"
                value={stats.scheduled}
                active={activeFilter === "scheduled"}
                onClick={() => handleFilterChange("scheduled")}
                position="middle"
                activeColor="blue"
              />
              <FilterCard
                label="Opened"
                value={stats.opened}
                active={activeFilter === "opened"}
                onClick={() => handleFilterChange("opened")}
                position="middle"
                activeColor="yellow"
              />
              <FilterCard
                label="Cancelled"
                value={stats.cancelled}
                active={activeFilter === "cancelled"}
                onClick={() => handleFilterChange("cancelled")}
                position="right"
                activeColor="grey"
              />
            </div>
          </div>

          {/* Table */}
          <ScrollArea className="min-h-0 flex-1">
            <div className="p-6">
              <div className="overflow-hidden rounded-lg border">
                <SimpleTable className="w-full">
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50 h-12">
                      {showCheckboxes && (
                        <TableHead className="w-10 border-0 px-3 py-0">
                          <Checkbox
                            checked={allSelectedOnPage ? true : someSelectedOnPage ? "indeterminate" : false}
                            onCheckedChange={(checked) => handleSelectAllOnPage(!!checked)}
                            aria-label="Select all on page"
                          />
                        </TableHead>
                      )}
                      <TableHead className="border-0 px-3 font-medium">Email</TableHead>
                      <TableHead className="border-0 px-3 font-medium">Household</TableHead>
                      {activeFilter === "opened" && (
                        <TableHead className="w-16 border-0 px-3 text-center font-medium">Opens</TableHead>
                      )}
                      {(activeFilter === "delivered" || activeFilter === "opened") && (
                        <TableHead className="border-0 px-3 font-medium">Delivered at</TableHead>
                      )}
                      {activeFilter === "scheduled" && (
                        <TableHead className="border-0 px-3 font-medium">Scheduled for</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRecipients.length > 0 ? (
                      paginatedRecipients.map((recipient) => (
                        <TableRow
                          key={recipient.id}
                          className={cn(
                            "h-12 cursor-pointer transition-colors",
                            previewRecipientId === recipient.id ? "bg-accent" : "hover:bg-accent/50"
                          )}
                          onClick={() => handleRowClick(recipient.id)}
                        >
                          {showCheckboxes && (
                            <TableCell
                              className="border-0 px-3 py-0"
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                checked={selectedIds.has(recipient.id)}
                                onCheckedChange={(checked) => handleSelectRow(recipient.id, !!checked)}
                                aria-label={`Select ${recipient.name}`}
                              />
                            </TableCell>
                          )}
                          <TableCell className="border-0 px-3">
                            <div className="max-w-48">
                              <span className="block truncate" title={recipient.email || undefined}>
                                {recipient.email || "-"}
                              </span>
                              {recipient.message_unavailable && (
                                <span className="text-muted-foreground block truncate text-xs">
                                  Message unavailable
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="border-0 px-3 whitespace-nowrap">
                            {recipient.household?.id && recipient.household?.type ? (
                              <PeekView
                                label={recipient.name}
                                recordType={recipient.household.type}
                                recordId={recipient.household.id}
                                size="sm"
                              />
                            ) : recipient.contact?.id ? (
                              <PeekView
                                label={recipient.name}
                                recordType="Contact"
                                recordId={recipient.contact.id}
                                size="sm"
                              />
                            ) : (
                              <span>{recipient.name}</span>
                            )}
                          </TableCell>
                          {activeFilter === "opened" && (
                            <TableCell className="text-muted-foreground border-0 px-3 text-center">
                              {recipient.open_count > 0 ? recipient.open_count : 0}
                            </TableCell>
                          )}
                          {(activeFilter === "delivered" || activeFilter === "opened") && (
                            <TableCell className="text-muted-foreground border-0 px-3">
                              {formatDateTimeShort(recipient.syncbox_draft?.sent_at)}
                            </TableCell>
                          )}
                          {activeFilter === "scheduled" && (
                            <TableCell className="text-muted-foreground border-0 px-3">
                              {formatDateTimeShort(recipient.syncbox_draft?.scheduled_for)}
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={activeFilter === "cancelled" ? 2 : activeFilter === "delivered" ? 3 : 4}
                          className="text-muted-foreground border-0 py-12 text-center"
                        >
                          No {activeFilter} emails
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </SimpleTable>
              </div>
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="flex items-center justify-between border-t px-6 py-4">
            <div className="flex items-center gap-4">
              <span className="text-muted-foreground text-sm">
                Showing {Math.min(currentPage * pageSize + 1, filteredRecipients.length)}-
                {Math.min((currentPage + 1) * pageSize, filteredRecipients.length)} of {filteredRecipients.length}{" "}
                results
              </span>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Per page</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(v) => {
                    setPageSize(Number(v))
                    setCurrentPage(0)
                  }}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="iconSm"
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                  >
                    <ChevronLeft />
                  </Button>
                  <span className="text-muted-foreground min-w-[80px] text-center text-sm">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="iconSm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                  >
                    <ChevronRight />
                  </Button>
                </div>
              )}
              {selectedIds.size > 0 && (
                <Button variant="outline" size="sm" onClick={handleCancelSelected} disabled={isCancellingSelected}>
                  {isCancellingSelected && <Loader2 className="animate-spin" />}
                  Cancel {selectedIds.size} selected
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </FullscreenDialog>
  )
}

interface FilterCardProps {
  label: string
  value: number
  active: boolean
  onClick: () => void
  position: "left" | "middle" | "right"
  activeColor?: "green" | "blue" | "yellow" | "grey"
}

function FilterCard({ label, value, active, onClick, position, activeColor = "green" }: FilterCardProps) {
  const activeColorClass = {
    green: "bg-green-background",
    blue: "bg-blue-background",
    yellow: "bg-yellow-background",
    grey: "bg-secondary",
  }[activeColor]

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-6 py-4 text-center transition-colors",
        active ? activeColorClass : "bg-background hover:bg-accent",
        position !== "left" && "border-l"
      )}
    >
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-muted-foreground text-sm">{label}</p>
    </button>
  )
}
